import React, { useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Trash2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetTimesheets = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [resetting, setResetting] = useState(false);
  const [result, setResult] = useState(null);

  const isAdmin = user?.role === 'admin';

  const handleReset = async () => {
    if (!window.confirm('Are you ABSOLUTELY sure? This will delete ALL timesheets, time entries, and payslips!')) {
      return;
    }

    setResetting(true);
    try {
      const response = await axios.delete(`${API}/timesheets/reset-all`);
      setResult(response.data);
      toast.success('All timesheets have been reset!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset timesheets');
    } finally {
      setResetting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8 bg-red-50 rounded-xl border border-red-200">
          <AlertTriangle className="mx-auto text-red-500 mb-4" size={48} />
          <h2 className="text-xl font-bold text-red-700">Access Denied</h2>
          <p className="text-red-600 mt-2">Only administrators can access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-12">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-red-50">
          <h2 className="text-2xl font-bold text-red-700 flex items-center gap-2">
            <AlertTriangle size={28} />
            Reset All Timesheets
          </h2>
          <p className="text-red-600 mt-2">This action cannot be undone!</p>
        </div>
        
        <div className="p-6 space-y-6">
          {!result ? (
            <>
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="font-semibold text-amber-800 mb-2">Warning: This will permanently delete:</p>
                <ul className="list-disc list-inside text-amber-700 space-y-1">
                  <li>All timesheet records</li>
                  <li>All time entries (clock-in/out records)</li>
                  <li>All payslips</li>
                </ul>
              </div>

              <p className="text-slate-600">
                Staff will need to create new timesheets and clock in fresh after this reset.
              </p>

              <div className="flex gap-4">
                <Button
                  variant="outline"
                  onClick={() => navigate('/timesheets')}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReset}
                  disabled={resetting}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {resetting ? (
                    <>Resetting...</>
                  ) : (
                    <>
                      <Trash2 size={18} className="mr-2" />
                      Yes, Reset Everything
                    </>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="mx-auto text-emerald-500 mb-4" size={64} />
              <h3 className="text-xl font-bold text-emerald-700 mb-4">Reset Complete!</h3>
              <div className="p-4 bg-slate-50 rounded-lg inline-block text-left">
                <p className="text-slate-700"><strong>Deleted:</strong></p>
                <ul className="text-slate-600 mt-2">
                  <li>Timesheets: {result.deleted?.timesheets || 0}</li>
                  <li>Time Entries: {result.deleted?.time_entries || 0}</li>
                  <li>Payslips: {result.deleted?.payslips || 0}</li>
                </ul>
              </div>
              <div className="mt-6">
                <Button onClick={() => navigate('/timesheets')}>
                  Go to Timesheets
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetTimesheets;
